// PURE pointer FSM (IMPLEMENTATION §2.7 "Pointer FSM", UX §3.3), table-driven from ui.json input. One active
// pointer id. The DOM glue (PointerController) feeds it timestamped events and the router's down decision
// (what the pointer went down on, for the current mode); it returns gesture outputs the router turns into
// Commands / FX / UI intents. Timers are pure too: `gestureDeadline` says when a `time` event is due, and
// every event first runs the deadlines it has passed, so a timeline gives the same outputs however late the
// timer fires.
//
//   IDLE --down--> (router's hitTest)
//     deposit & PLAY                 → out hit; HOLD_PENDING(holdStartMs)
//     gallery & COLD_OPEN            → TAP_ONLY (out fire on up, ≥ coldOpenMinGapMs apart)
//     Chute tool & (empty|chute|bldg)→ PAINT        Sell tool & chute → ERASE
//     draggable(mode, obj)           → PRESS_PENDING(longPressMs)
//     non-draggable                  → PRESS_PENDING(noDrag)
//   PRESS_PENDING --move>slop--> draggable ? DRAG (out dragStart; the router sends grab) : out noDrag
//   PRESS_PENDING --up (< slop; an up past the slop is a move first)--> out tap
//                 --longPressMs--> out longPress (LONG_PRESS: up → nothing;
//                                                    move>slop & draggable → DRAG)
//   HOLD_PENDING --holdStartMs--> HOLD (out hold); --move>slop / leave cell--> no hold (swallowed)
//   HOLD --up / leave cell / move>holdSlop / cancel--> out holdEnd. A hold never turns into a drag.
//   DRAG --move--> out dragMove   --up--> out drop   --cancel--> out dragCancel
//   PAINT / ERASE --move--> out paintMove / eraseMove (the router builds the path) --up--> …End
//   2nd pointer: cancels a pending press (palm guard); ignored during a drag, hold, paint or erase.
//   Tap-rate cap: a pointerdown within 1000/tapRateCapPerSec ms of the previous one AND within doubleTapPx of
//   it is jitter: the whole gesture is ignored.
//   Double tap: the 2nd pointerdown ≤ doubleTapMs after the 1st tap's up, ≤ doubleTapPx away, same object →
//   that tap is `double` (the 1st tap already selected; the 2nd upgrades to Quick Merge).
//   Adopt (§2.8 shop handoff): IDLE --adopt--> DRAG (out dragStart adopted; no grab).

import type { Cell } from '../layout/computeLayout.ts';

export interface GestureCfg {
  slopTouch: number;
  slopMouse: number;
  longPressMs: number;
  holdStartMs: number;
  holdSlop: number;
  doubleTapMs: number;
  doubleTapPx: number;
  tapRateCapPerSec: number;
  coldOpenMinGapMs: number;
}

/** ui.json `input` → the FSM table. */
export function gestureCfg(i: GestureCfg): GestureCfg {
  return {
    slopTouch: i.slopTouch,
    slopMouse: i.slopMouse,
    longPressMs: i.longPressMs,
    holdStartMs: i.holdStartMs,
    holdSlop: i.holdSlop,
    doubleTapMs: i.doubleTapMs,
    doubleTapPx: i.doubleTapPx,
    tapRateCapPerSec: i.tapRateCapPerSec,
    coldOpenMinGapMs: i.coldOpenMinGapMs,
  };
}

/** What the pointer went down on, decided by the router for the current mode (the §2.7 IDLE table). */
export type DownKind =
  /** A deposit in Play: `hit` now, `hold` after holdStartMs. */
  | { k: 'deposit'; cell: Cell }
  /** The Gallery during the cold open: fires on pointerup. */
  | { k: 'tapOnly'; obj: string }
  /** Build + Chute tool on empty floor, a chute or a building: a chute stroke. */
  | { k: 'paint'; cell: Cell }
  /** Build + Sell tool on a chute: an erase stroke. */
  | { k: 'erase'; cell: Cell }
  /** Anything else. `obj` identifies the object (double tap = same object). */
  | { k: 'press'; obj: string; draggable: boolean; longPress: boolean };

/** `overlay`: a blocking overlay came up (context lost, Elsewhere, ErrorScreen; main.ts). */
export type CancelReason =
  | 'pointercancel'
  | 'lostpointercapture'
  | 'blur'
  | 'hidden'
  | 'mode'
  | 'overlay';

export type GestureEvent =
  | { k: 'down'; id: number; x: number; y: number; t: number; mouse: boolean; kind: DownKind }
  /** `cell` = the board cell under the finger (hold: leaving the deposit cell; paint strokes). */
  | { k: 'move'; id: number; x: number; y: number; t: number; cell: Cell | null }
  | { k: 'up'; id: number; x: number; y: number; t: number }
  /** `id` null = whatever is active (blur, hidden page, mode switch). */
  | { k: 'cancel'; id: number | null; t: number; reason: CancelReason }
  | { k: 'time'; t: number }
  /** §2.8: a DOM shop card (or card slot) hands its pointer to the input root. */
  | { k: 'adopt'; id: number; x: number; y: number; t: number; mouse: boolean };

export type GestureOut =
  | { k: 'hit'; cell: Cell }
  | { k: 'hold'; cell: Cell }
  | { k: 'holdEnd' }
  /** TAP(obj): at the pointerdown position (hitTest on down, §2.7), even if the finger rolled under the slop. */
  | { k: 'tap'; x: number; y: number; obj: string; double: boolean }
  | { k: 'longPress'; x: number; y: number; obj: string }
  /** Cold-open Gallery tap (pointerup, ≥ coldOpenMinGapMs after the previous fire). */
  | { k: 'fire'; x: number; y: number; obj: string }
  /** DRAG starts at (x, y); (x0, y0) = where the press began. `adopted` = shop handoff (no grab). */
  | { k: 'dragStart'; x: number; y: number; x0: number; y0: number; adopted: boolean }
  | { k: 'dragMove'; x: number; y: number }
  | { k: 'drop'; x: number; y: number }
  | { k: 'dragCancel'; reason: CancelReason }
  /** A non-draggable press moved past the slop: CANCEL (the router may show "Build mode only"). */
  | { k: 'noDrag'; x: number; y: number; obj: string }
  | { k: 'paintStart'; cell: Cell; x: number; y: number }
  | { k: 'paintMove'; cell: Cell | null; x: number; y: number }
  | { k: 'paintEnd' }
  | { k: 'paintCancel'; reason: CancelReason }
  | { k: 'eraseStart'; cell: Cell }
  | { k: 'eraseMove'; cell: Cell | null }
  | { k: 'eraseEnd' }
  | { k: 'eraseCancel'; reason: CancelReason }
  /** Diagnostics: rate = tap-rate cap, palm = a 2nd pointer cancelled a pending press, busy = a 2nd
   * pointer during a drag / hold / paint / erase (or an adopt while busy), gap = cold-open fire too soon. */
  | { k: 'ignored'; why: 'rate' | 'palm' | 'busy' | 'gap' };

interface P {
  id: number;
  x0: number;
  y0: number;
  t0: number;
  mouse: boolean;
}

type Active =
  | (P & { k: 'holdPending'; cell: Cell })
  | (P & { k: 'hold'; cell: Cell })
  | (P & { k: 'tapOnly'; obj: string })
  | (P & {
      k: 'press';
      obj: string;
      draggable: boolean;
      longPress: boolean;
      /** The long-press fired (LONG_PRESS). */
      long: boolean;
      /** This press is the 2nd of a double tap. */
      double: boolean;
    })
  | { k: 'drag'; id: number; mouse: boolean; adopted: boolean }
  | { k: 'paint'; id: number }
  | { k: 'erase'; id: number }
  /** Ignored until it lifts (rate-capped, palm-cancelled, a hold that ended, a no-drag press). */
  | { k: 'swallow'; id: number };

export interface GestureState {
  a: Active | null;
  /** The last tap (for double tap): its down position and up time. */
  lastTap: { t: number; x: number; y: number; obj: string } | null;
  /** The last accepted pointerdown (tap-rate cap). */
  lastDown: { t: number; x: number; y: number } | null;
  /** The last cold-open fire (ms). */
  lastFire: number | null;
}

export const GESTURE_IDLE: GestureState = {
  a: null,
  lastTap: null,
  lastDown: null,
  lastFire: null,
};

export type GestureStateKind = Active['k'] | 'idle';

export function gestureKind(s: GestureState): GestureStateKind {
  return s.a === null ? 'idle' : s.a.k;
}

export function activePointer(s: GestureState): number | null {
  return s.a === null ? null : s.a.id;
}

const dist = (ax: number, ay: number, bx: number, by: number): number =>
  Math.hypot(ax - bx, ay - by);

const sameCell = (a: Cell | null, b: Cell): boolean => a !== null && a.x === b.x && a.y === b.y;

/** When the next `time` event is due (ms), or null when no timer runs. */
export function gestureDeadline(s: GestureState, cfg: GestureCfg): number | null {
  const a = s.a;
  if (a === null) return null;
  if (a.k === 'holdPending') return a.t0 + cfg.holdStartMs;
  if (a.k === 'press' && a.longPress && !a.long) return a.t0 + cfg.longPressMs;
  return null;
}

interface Step {
  s: GestureState;
  out: GestureOut[];
}

/** Runs the timers due at `t`. */
function runTimers(s: GestureState, t: number, cfg: GestureCfg, out: GestureOut[]): GestureState {
  const a = s.a;
  if (a === null) return s;
  if (a.k === 'holdPending' && t >= a.t0 + cfg.holdStartMs) {
    out.push({ k: 'hold', cell: a.cell });
    return { ...s, a: { ...a, k: 'hold' } };
  }
  if (a.k === 'press' && a.longPress && !a.long && t >= a.t0 + cfg.longPressMs) {
    out.push({ k: 'longPress', x: a.x0, y: a.y0, obj: a.obj });
    return { ...s, a: { ...a, long: true } };
  }
  return s;
}

function onDown(
  s: GestureState,
  e: Extract<GestureEvent, { k: 'down' }>,
  cfg: GestureCfg,
  out: GestureOut[],
): GestureState {
  const a = s.a;
  if (a !== null) {
    if (a.id === e.id) return s;
    // palm guard: a 2nd pointer cancels a pending press; during a drag, hold, paint or erase it is ignored
    if (a.k === 'press' || a.k === 'holdPending' || a.k === 'tapOnly') {
      out.push({ k: 'ignored', why: 'palm' });
      return { ...s, a: { k: 'swallow', id: a.id } };
    }
    out.push({ k: 'ignored', why: 'busy' });
    return s;
  }
  // tap-rate cap: a down too soon after the previous one, at the same spot, is jitter
  const ld = s.lastDown;
  if (
    ld !== null &&
    e.t - ld.t < 1000 / cfg.tapRateCapPerSec &&
    dist(e.x, e.y, ld.x, ld.y) <= cfg.doubleTapPx
  ) {
    out.push({ k: 'ignored', why: 'rate' });
    return { ...s, a: { k: 'swallow', id: e.id } };
  }
  const p: P = { id: e.id, x0: e.x, y0: e.y, t0: e.t, mouse: e.mouse };
  const next: GestureState = { ...s, lastDown: { t: e.t, x: e.x, y: e.y } };
  const k = e.kind;
  switch (k.k) {
    case 'deposit':
      out.push({ k: 'hit', cell: k.cell });
      return { ...next, a: { ...p, k: 'holdPending', cell: k.cell } };
    case 'tapOnly':
      return { ...next, a: { ...p, k: 'tapOnly', obj: k.obj } };
    case 'paint':
      out.push({ k: 'paintStart', cell: k.cell, x: e.x, y: e.y });
      return { ...next, a: { k: 'paint', id: e.id } };
    case 'erase':
      out.push({ k: 'eraseStart', cell: k.cell });
      return { ...next, a: { k: 'erase', id: e.id } };
    case 'press': {
      const lt = s.lastTap;
      const double =
        lt !== null &&
        e.t - lt.t <= cfg.doubleTapMs &&
        dist(e.x, e.y, lt.x, lt.y) <= cfg.doubleTapPx &&
        lt.obj === k.obj;
      return {
        ...next,
        a: {
          ...p,
          k: 'press',
          obj: k.obj,
          draggable: k.draggable,
          longPress: k.longPress,
          long: false,
          double,
        },
      };
    }
  }
}

function onMove(
  s: GestureState,
  e: Extract<GestureEvent, { k: 'move' }>,
  cfg: GestureCfg,
  out: GestureOut[],
): GestureState {
  const a = s.a;
  if (a === null || a.id !== e.id) return s;
  switch (a.k) {
    case 'holdPending': {
      const slop = a.mouse ? cfg.slopMouse : cfg.slopTouch;
      if (dist(e.x, e.y, a.x0, a.y0) > slop || !sameCell(e.cell, a.cell))
        return { ...s, a: { k: 'swallow', id: a.id } }; // no hold starts (and never a drag)
      return s;
    }
    case 'hold':
      if (dist(e.x, e.y, a.x0, a.y0) > cfg.holdSlop || !sameCell(e.cell, a.cell)) {
        out.push({ k: 'holdEnd' });
        return { ...s, a: { k: 'swallow', id: a.id } };
      }
      return s;
    case 'tapOnly': {
      const slop = a.mouse ? cfg.slopMouse : cfg.slopTouch;
      return dist(e.x, e.y, a.x0, a.y0) > slop ? { ...s, a: { k: 'swallow', id: a.id } } : s;
    }
    case 'press': {
      const slop = a.mouse ? cfg.slopMouse : cfg.slopTouch;
      if (dist(e.x, e.y, a.x0, a.y0) <= slop) return s;
      if (a.draggable) {
        out.push({ k: 'dragStart', x: e.x, y: e.y, x0: a.x0, y0: a.y0, adopted: false });
        return { ...s, a: { k: 'drag', id: a.id, mouse: a.mouse, adopted: false } };
      }
      if (a.long) return s; // LONG_PRESS on a non-draggable object: stay (the card stays open)
      out.push({ k: 'noDrag', x: a.x0, y: a.y0, obj: a.obj });
      return { ...s, a: { k: 'swallow', id: a.id } };
    }
    case 'drag':
      out.push({ k: 'dragMove', x: e.x, y: e.y });
      return s;
    case 'paint':
      out.push({ k: 'paintMove', cell: e.cell, x: e.x, y: e.y });
      return s;
    case 'erase':
      out.push({ k: 'eraseMove', cell: e.cell });
      return s;
    case 'swallow':
      return s;
  }
}

function onUp(
  s: GestureState,
  e: Extract<GestureEvent, { k: 'up' }>,
  cfg: GestureCfg,
  out: GestureOut[],
): GestureState {
  const a = s.a;
  if (a === null || a.id !== e.id) return s;
  if (a.k === 'press' || a.k === 'tapOnly') {
    // a tap is "up < slop" (§2.7): the up point counts as a move first, so an up far from the down with no
    // pointermove in between (coalesced / synthetic events) is a drag + drop or a cancel, never a tap
    const moved = onMove(s, { k: 'move', id: e.id, x: e.x, y: e.y, t: e.t, cell: null }, cfg, out);
    if (moved.a !== a) return onUp(moved, e, cfg, out);
  }
  const idle: GestureState = { ...s, a: null };
  switch (a.k) {
    case 'holdPending':
      return idle; // the pointerdown hit was the tap
    case 'hold':
      out.push({ k: 'holdEnd' });
      return idle;
    case 'tapOnly':
      if (s.lastFire !== null && e.t - s.lastFire < cfg.coldOpenMinGapMs) {
        out.push({ k: 'ignored', why: 'gap' });
        return idle;
      }
      out.push({ k: 'fire', x: a.x0, y: a.y0, obj: a.obj });
      return { ...idle, lastFire: e.t };
    case 'press':
      if (a.long) return idle; // LONG_PRESS --up--> IDLE (the card stays)
      out.push({ k: 'tap', x: a.x0, y: a.y0, obj: a.obj, double: a.double });
      // a double tap is consumed: a third tap starts a new pair
      return { ...idle, lastTap: a.double ? null : { t: e.t, x: a.x0, y: a.y0, obj: a.obj } };
    case 'drag':
      out.push({ k: 'drop', x: e.x, y: e.y });
      return idle;
    case 'paint':
      out.push({ k: 'paintEnd' });
      return idle;
    case 'erase':
      out.push({ k: 'eraseEnd' });
      return idle;
    case 'swallow':
      return idle;
  }
}

function onCancel(
  s: GestureState,
  e: Extract<GestureEvent, { k: 'cancel' }>,
  out: GestureOut[],
): GestureState {
  const a = s.a;
  if (a === null || (e.id !== null && a.id !== e.id)) return s;
  // lostpointercapture only matters for a drag (a capture we did not release ourselves)
  if (e.reason === 'lostpointercapture' && a.k !== 'drag') return s;
  const idle: GestureState = { ...s, a: null };
  switch (a.k) {
    case 'hold':
      out.push({ k: 'holdEnd' });
      break;
    case 'drag':
      out.push({ k: 'dragCancel', reason: e.reason });
      break;
    case 'paint':
      out.push({ k: 'paintCancel', reason: e.reason });
      break;
    case 'erase':
      out.push({ k: 'eraseCancel', reason: e.reason });
      break;
    default:
      break;
  }
  return idle;
}

/** One event → the next state and the gesture outputs, in order. */
export function gestureStep(s: GestureState, e: GestureEvent, cfg: GestureCfg): Step {
  const out: GestureOut[] = [];
  let st = runTimers(s, e.t, cfg, out);
  switch (e.k) {
    case 'time':
      break;
    case 'down':
      st = onDown(st, e, cfg, out);
      break;
    case 'move':
      st = onMove(st, e, cfg, out);
      break;
    case 'up':
      st = onUp(st, e, cfg, out);
      break;
    case 'cancel':
      st = onCancel(st, e, out);
      break;
    case 'adopt':
      if (st.a !== null) {
        out.push({ k: 'ignored', why: 'busy' });
        break;
      }
      out.push({ k: 'dragStart', x: e.x, y: e.y, x0: e.x, y0: e.y, adopted: true });
      st = { ...st, a: { k: 'drag', id: e.id, mouse: e.mouse, adopted: true } };
      break;
  }
  return { s: st, out };
}
